import { Component, EventEmitter, OnDestroy, OnInit, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ApiService } from '../../service/api.service';
import { CartService } from '../../service/cart.service';
import { PaymentRequest, PaymentRecordRequest } from '../../service/api.dto';
import { Subscription, interval, timer } from 'rxjs';
import { environment } from '../../../environments/environment';

@Component({
  selector: 'app-step6-payment',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './step6-payment.component.html',
  styleUrl: './step6-payment.component.scss'
})
export class Step6PaymentComponent implements OnInit, OnDestroy {
  @Output() next = new EventEmitter<void>();
  @Output() previous = new EventEmitter<void>();
  loading = true;
  error: string | null = null;
  waitingForPayment = false; // Indica si estamos esperando que el cliente pague
  private paymentId: string | null = null;
  private transactionId: string | null = null;
  private pollingSubscription?: Subscription;
  private timeoutSubscription?: Subscription;
  private readonly POLLING_INTERVAL = 2000; // Verificar cada 2 segundos
  private readonly TIMEOUT_DURATION = 40000; // 40 segundos

  constructor(
    private apiService: ApiService,
    private cartService: CartService
  ) {}

  ngOnInit(): void {
    this.initiatePayment();
  }

  ngOnDestroy(): void {
    if (this.pollingSubscription) {
      this.pollingSubscription.unsubscribe();
    }
    if (this.timeoutSubscription) {
      this.timeoutSubscription.unsubscribe();
    }
  }

  initiatePayment(): void {
    // Limpiar estado y detener procesos anteriores
    this.stopPolling();
    this.stopTimeout();
    this.error = null;
    this.loading = true;
    this.waitingForPayment = false;
    this.paymentId = null;
    
    const totalAmount = this.cartService.getTotalPrice();
    
    if (totalAmount <= 0) {
      this.error = 'El carrito está vacío';
      this.loading = false;
      return;
    }

    // Validar que tengamos el pos_id
    if (!this.apiService.details?.id) {
      this.error = 'Error: No se pudo obtener la información del punto de venta';
      this.loading = false;
      return;
    }

    // Paso 1: Crear el pago en pendiente en el backend
    const paymentRecord: PaymentRecordRequest = {
      pos_id: this.apiService.details.id,
      amount: totalAmount,
      currency: 'ARS',
      method: 'PAY_WAY',
      external_id: '', // Se actualizará después con el transactionId de PAXAREL
      description: 'Compra en línea',
      metadata: {}
    };

    this.apiService.recordPayment(paymentRecord).subscribe({
      next: (paymentRecordResponse) => {
        // console.log('[Step6 Payment] Respuesta del registro de pago:', paymentRecordResponse);
        this.paymentId = paymentRecordResponse.id;
        // console.log('Paxarel API Key', this.apiService.details?.paxarel_api_key);
        // console.log('Paxarel Payment Method', this.apiService.details?.paxarel_payment_method);
        // Paso 2: Crear el pago en PAXAREL usando el ID del backend como externalReference
        const paymentRequest: PaymentRequest = {
          apiKey: this.apiService.details?.paxarel_api_key || environment.paxarelApiKey,
          method: this.apiService.details?.paxarel_payment_method || 'PAYWAY_POS',
          item: {
            name: 'Symtrom POS sale',
            amount: totalAmount,
            successUrl: window.location.origin + '/success',
            failUrl: window.location.origin + '/error',
            currency: 'ARS'
          },
          externalReference: paymentRecordResponse.id
        };

        // console.log('[Step6 Payment] Creando pago en PAXAREL con request:', paymentRequest);

        this.apiService.createPayment(paymentRequest).subscribe({
          next: (paxarelResponse) => {
            // console.log('[Step6 Payment] Respuesta completa de PAXAREL:', paxarelResponse);
            // console.log('[Step6 Payment] paymentUrl recibido:', paxarelResponse.paymentUrl);
            // console.log('[Step6 Payment] transactionId recibido:', paxarelResponse.transactionId);
            this.transactionId = paxarelResponse.transactionId;
            
            this.loading = false;
            
            // Abrir la URL de pago en una nueva ventana
            const paymentWindow = window.open(paxarelResponse.paymentUrl, '_blank');
            
            if (!paymentWindow) {
              console.warn('[Step6 Payment] Advertencia: No se pudo abrir la ventana de pago (puede estar bloqueada por el navegador)');
              this.error = 'No se pudo abrir la ventana de pago. Por favor, permite las ventanas emergentes e intenta nuevamente.';
              return;
            }
            
            // console.log('[Step6 Payment] Ventana de pago abierta correctamente');
            
            // Marcar que estamos esperando el pago
            this.waitingForPayment = true;
            
            // Iniciar polling para verificar el estado del pago
            this.startPaymentPolling();
            
            // Configurar timeout de 40 segundos
            this.startTimeout();
          },
          error: (paxarelErr) => {
            this.loading = false;
            this.error = 'Error al procesar el pago con la pasarela. Por favor, intente nuevamente.';
            console.error('[Step6 Payment] Error al crear el pago en PAXAREL:', paxarelErr);
            console.error('[Step6 Payment] Detalles del error:', JSON.stringify(paxarelErr, null, 2));
          }
        });
      },
      error: (recordErr) => {
        this.loading = false;
        this.error = 'Error al crear el registro de pago. Por favor, intente nuevamente.';
        console.error('[Step6 Payment] Error al registrar el pago en el backend:', recordErr);
        console.error('[Step6 Payment] Detalles del error:', JSON.stringify(recordErr, null, 2));
      }
    });
  }

  private startPaymentPolling(): void {
    if (!this.paymentId) {
      console.error('[Step6 Payment] No hay paymentId para hacer polling');
      return;
    }

    // console.log('[Step6 Payment] Iniciando polling del estado del pago para ID:', this.paymentId);

    this.pollingSubscription = interval(this.POLLING_INTERVAL).subscribe(() => {
      this.checkPaymentStatus();
    });
  }

  private checkPaymentStatus(): void {
    if (!this.transactionId) {
      return;
    }

    // console.log('[Step6 Payment] Verificando estado del pago en Paxarel para transactionId:', this.transactionId);

    this.apiService.getPaxarelPaymentStatus(this.transactionId).subscribe({
      next: (paymentStatus) => {
        // console.log('[Step6 Payment] Estado del pago recibido:', paymentStatus);
        
        // Verificar si el pago está completado o aprobado (ajustar según la estructura de respuesta del backend)
        const status = paymentStatus?.status || paymentStatus?.state || paymentStatus?.paymentStatus;
        const normalizedStatus = (status || '').toString().toUpperCase();
        const isPaid = (
          normalizedStatus === 'APPROVED' ||
          normalizedStatus === 'PAID' ||
          normalizedStatus === 'COMPLETED' ||
          paymentStatus?.paid === true
        );
        const isRejected = (
          normalizedStatus === 'REJECTED' ||
          normalizedStatus === 'FAILED'
        );

        if (isPaid) {
          // console.log('[Step6 Payment] ¡Pago confirmado! Avanzando al siguiente paso.');
          this.waitingForPayment = false;
          this.stopPolling();
          this.stopTimeout();
          // Solo avanzar cuando el pago esté confirmado
          this.next.emit();
        } else if (isRejected) {
          // console.log('[Step6 Payment] Pago rechazado o fallido. Volviendo al carrito.');
          this.waitingForPayment = false;
          this.stopPolling();
          this.stopTimeout();
          // Guardar estado para informar al usuario en el carrito
          sessionStorage.setItem('paymentFailed', 'true');
          // Volver al paso del carrito
          this.previous.emit();
        } else {
          // console.log('[Step6 Payment] Pago aún pendiente. Estado actual:', paymentStatus.status || paymentStatus.state);
        }
      },
      error: (error) => {
        console.error('[Step6 Payment] Error al verificar el estado del pago:', error);
        // No detenemos el polling en caso de error, continuamos intentando
      }
    });
  }

  private startTimeout(): void {
    // console.log('[Step6 Payment] Iniciando timeout de 40 segundos');
    
    this.timeoutSubscription = timer(this.TIMEOUT_DURATION).subscribe(() => {
      // console.log('[Step6 Payment] Timeout alcanzado (40 segundos). Volviendo al paso 5.');
      this.waitingForPayment = false;
      this.stopPolling();
      this.stopTimeout();
      this.error = 'El tiempo de espera ha expirado. Por favor, intenta nuevamente.';
      // Marcar en sessionStorage que se vuelve por timeout
      sessionStorage.setItem('paymentTimeout', 'true');
      // Volver al paso 5
      this.previous.emit();
    });
  }

  private stopPolling(): void {
    if (this.pollingSubscription) {
      this.pollingSubscription.unsubscribe();
      this.pollingSubscription = undefined;
      // console.log('[Step6 Payment] Polling detenido');
    }
  }

  private stopTimeout(): void {
    if (this.timeoutSubscription) {
      this.timeoutSubscription.unsubscribe();
      this.timeoutSubscription = undefined;
      // console.log('[Step6 Payment] Timeout detenido');
    }
  }

  // Método para saltar al siguiente paso en modo demo (mock)
  skipToNextStep(): void {
    if (this.waitingForPayment) {
      // console.log('[Step6 Payment] Saltando al siguiente paso (modo demo/mock)');
      this.waitingForPayment = false;
      this.stopPolling();
      this.stopTimeout();
      this.next.emit();
    }
  }
}
